/**
 * Parses OpenSearch aggregation responses into flat table rows.
 *
 * Handles nested terms aggregation buckets and extracts metric sub-aggregation values.
 * Converts the tree structure back into a flat array of row objects suitable for table rendering.
 *
 * Example input (response.aggregations):
 * {
 *   "group_by_company": {
 *     "buckets": [
 *       {
 *         "key": "Acme Corp",
 *         "doc_count": 42,
 *         "group_by_status": {
 *           "buckets": [
 *             {
 *               "key": "active",
 *               "doc_count": 30,
 *               "agg_amount": { "value": 15000 },
 *               "agg_name": { "value": 30 }
 *             }
 *           ]
 *         }
 *       }
 *     ]
 *   }
 * }
 *
 * Example output:
 * [
 *   { company: "Acme Corp", status: "active", amount: 15000, name: 30 }
 * ]
 */

import type { ColumnAggregationMap } from './aggregation-types';

interface AggBucket {
  key: string | number;
  key_as_string?: string;
  keyAsString?: string;
  doc_count: number;
  docCount?: number;
  [subAggName: string]: unknown;
}

interface AggResult {
  buckets?: AggBucket[];
  value?: number | null;
  [key: string]: unknown;
}

/**
 * Convert a snake_case agg name to camelCase to match backend's deep_camelize_keys.
 * e.g., "group_by_employeeNameKeyword" → "groupByEmployeeNameKeyword"
 */
function snakeToCamel(str: string): string {
  return str.replace(/_([a-zA-Z])/g, (_, letter: string) => letter.toUpperCase());
}

/** Find an aggregation result by name, trying both snake_case and camelCase keys */
function findAgg(obj: Record<string, unknown>, snakeName: string): AggResult | undefined {
  return (obj[snakeName] ?? obj[snakeToCamel(snakeName)]) as AggResult | undefined;
}

/**
 * Flatten nested aggregation buckets into flat row objects.
 *
 * @param aggregations - The `aggregations` object from the OpenSearch response
 * @param groupByKeys - ordered array of group-by field keys
 * @param columnAggregations - map of columnKey → AggregationType
 * @returns Array of flat row objects with group-by keys and metric values
 */
export function flattenAggBuckets(
  aggregations: Record<string, unknown>,
  groupByKeys: string[],
  columnAggregations: ColumnAggregationMap
): Record<string, unknown>[] {
  if (!aggregations || groupByKeys.length === 0) return [];

  const rows: Record<string, unknown>[] = [];
  const firstKey = groupByKeys[0]!;
  const topAgg = findAgg(aggregations, `group_by_${firstKey}`);

  if (!topAgg?.buckets) return [];

  recurseFlatten(topAgg.buckets, groupByKeys, 0, {}, columnAggregations, rows);
  return rows;
}

/**
 * Recursively walk nested buckets, building row objects.
 */
function recurseFlatten(
  buckets: AggBucket[],
  groupByKeys: string[],
  depth: number,
  parentRow: Record<string, unknown>,
  columnAggregations: ColumnAggregationMap,
  rows: Record<string, unknown>[]
): void {
  const fieldKey = groupByKeys[depth]!;

  for (const bucket of buckets) {
    const row = {
      ...parentRow,
      [fieldKey]: bucket.key_as_string ?? bucket.keyAsString ?? bucket.key,
    };

    const nextDepth = depth + 1;

    if (nextDepth < groupByKeys.length) {
      // There are more group-by levels — recurse into the next nested agg
      const nextKey = groupByKeys[nextDepth]!;
      const nextAgg = findAgg(bucket as Record<string, unknown>, `group_by_${nextKey}`);

      if (nextAgg?.buckets) {
        recurseFlatten(nextAgg.buckets, groupByKeys, nextDepth, row, columnAggregations, rows);
      }
    } else {
      // Innermost level — extract metric sub-aggregation values
      for (const [colKey, _aggType] of Object.entries(columnAggregations)) {
        // Skip group-by fields (they're already in the row from bucket keys)
        if (groupByKeys.includes(colKey)) continue;

        const metricAgg = findAgg(bucket as Record<string, unknown>, `agg_${colKey}`);
        if (metricAgg !== undefined && metricAgg !== null) {
          // For count-type aggs, fall back to doc_count if value is null
          row[colKey] = metricAgg.value ?? bucket.doc_count ?? bucket.docCount;
        } else {
          row[colKey] = bucket.doc_count ?? bucket.docCount;
        }
      }

      rows.push(row);
    }
  }
}
